import React, { useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

export interface ColumnResizerProps {
  /** Called continuously while the user drags. Receives the new width in px. */
  onResize: (newWidth: number) => void
  /** Called once when the user releases the mouse. */
  onResizeEnd?: (finalWidth: number) => void
  /** Starting width before drag (typically the column's current rendered width). */
  initialWidth: number
  /** Lower bound clamp. Default 60px so columns can't disappear. */
  minWidth?: number
  /** Upper bound clamp. Default 800px. */
  maxWidth?: number
  className?: string
}

/**
 * A 6px-wide vertical handle attached to the right edge of a table header
 * cell. Drag horizontally to resize the column. The handle is absolutely
 * positioned, so the parent `TableHead` must be `position: relative`.
 *
 * Implementation notes:
 *   - We track the drag with refs (no React state during the drag) to avoid
 *     re-rendering the entire table 60 times a second.
 *   - onMouseUp removes both listeners; cleanup on unmount also handles edge
 *     cases (component unmounted mid-drag).
 *   - The handle stops mousedown propagation so it never triggers the column's
 *     onSort handler that sits next to it.
 */
export function ColumnResizer({
  onResize,
  onResizeEnd,
  initialWidth,
  minWidth = 60,
  maxWidth = 800,
  className,
}: ColumnResizerProps) {
  const { t } = useTranslation('common')
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)
  const currentWidthRef = useRef(0)
  const draggingRef = useRef(false)

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!draggingRef.current) return
      const dx = e.clientX - startXRef.current
      const next = Math.max(minWidth, Math.min(maxWidth, startWidthRef.current + dx))
      currentWidthRef.current = next
      onResize(next)
    },
    [minWidth, maxWidth, onResize],
  )

  const handleMouseUp = useCallback(() => {
    if (!draggingRef.current) return
    draggingRef.current = false
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    onResizeEnd?.(currentWidthRef.current)
  }, [handleMouseMove, onResizeEnd])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Block parent onClick (which would toggle sort).
      e.stopPropagation()
      e.preventDefault()
      startXRef.current = e.clientX
      startWidthRef.current = initialWidth
      currentWidthRef.current = initialWidth
      draggingRef.current = true
      // Prevent text selection during drag and keep the resize cursor visible
      // across the entire document, not just on the handle.
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [initialWidth, handleMouseMove, handleMouseUp],
  )

  // If the component unmounts mid-drag, clean up the global listeners.
  useEffect(() => {
    return () => {
      if (draggingRef.current) {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
  }, [handleMouseMove, handleMouseUp])

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('ui.columnResizer.resizeColumn')}
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
      className={
        'absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-border transition-colors ' +
        (className ?? '')
      }
    />
  )
}
